import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { scansApi, type ScanDetail } from '../api/scans.ts';
import { authApi } from '../api/auth.ts';
import { useAuthStore } from '../state/authStore.ts';
import {
  readAnonymousScanToken,
  readTokenFromSearch,
  storeAnonymousScanToken,
} from '../state/anonymousScans.ts';
import { ApiError } from '../api/client.ts';
import { useSeo } from '../hooks/useSeo.ts';
import BadgeEmbedSection from '../components/BadgeEmbedSection.tsx';
import DomainVerificationPanel from '../components/DomainVerificationPanel.tsx';

export default function ScanResultPage() {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  useSeo({
    title: 'Accessibility report',
    noindex: true,
  });

  // Resolve the access token (anonymous flow only). Priority:
  //   1. URL hash ?t= / ?token= (visitor clicked the emailed link or shared the URL)
  //   2. localStorage keyed by scan id (visitor started the scan on this browser)
  // If we find a URL-hash token, we also persist it so a subsequent reload without the
  // query string still works.
  const accessToken = useMemo(() => {
    if (!id) return undefined;
    const fromUrl = readTokenFromSearch(location.search);
    if (fromUrl) {
      storeAnonymousScanToken(id, fromUrl);
      return fromUrl;
    }
    return readAnonymousScanToken(id) ?? undefined;
  }, [id, location.search]);

  const setUser = useAuthStore(s => s.setUser);
  const refreshedRef = useRef(false);
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ['scan', id, accessToken ?? 'owned'],
    queryFn: () => scansApi.get(id!, accessToken),
    enabled: Boolean(id),
    refetchInterval: q => {
      const status = q.state.data?.status;
      return status && status !== 'Completed' && status !== 'Failed' ? 2000 : false;
    },
  });

  // For owned scans only: refresh /me on first completion so the NavBar credit badge updates.
  // Anonymous scans never debit credits, so there's nothing to refresh.
  useEffect(() => {
    if (data?.viewMode === 'owned' && data.status === 'Completed' && !refreshedRef.current) {
      refreshedRef.current = true;
      authApi.me().then(setUser).catch(() => { /* silent — stale UI is acceptable */ });
    }
  }, [data?.status, data?.viewMode, setUser]);

  if (isLoading) return <div className="page"><p>Loading scan…</p></div>;
  if (error) return <div className="page"><p className="error">Couldn't load scan.</p></div>;
  if (!data) return null;

  if (data.status === 'Failed') {
    return (
      <div className="page">
        <h1>Scan failed</h1>
        <p className="error">{data.errorMessage ?? 'Something went wrong.'}</p>
        <Link to="/" className="cta">Try another URL</Link>
      </div>
    );
  }

  if (data.status !== 'Completed') {
    return <ScanInProgressView data={data} />;
  }

  if (data.viewMode === 'anonymous_locked') {
    return <AnonymousScanSummaryView data={data} accessToken={accessToken!} />;
  }

  return <FullScanReportView data={data} accessToken={accessToken} queryClient={queryClient} />;
}

/* ──────────────────────────────────────────────────────────────────────────── */

function ScanInProgressView({ data }: { data: ScanDetail }) {
  const hasProgressBar = data.progressTotal > 0 && data.progressCurrent > 0;
  const pct = hasProgressBar
    ? Math.min(100, Math.round((data.progressCurrent / data.progressTotal) * 100))
    : null;

  return (
    <div className="page scan-progress">
      <h1>Scanning {data.targetUrl}…</h1>
      <p className="progress-step">
        <strong>{data.progressMessage ?? data.status}</strong>
      </p>
      {hasProgressBar && (
        <>
          <div
            className="progress-bar"
            role="progressbar"
            aria-valuenow={pct ?? 0}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label={`Scanning page ${data.progressCurrent} of ${data.progressTotal}`}
          >
            <div className="progress-bar-fill" style={{ width: `${pct}%` }} />
          </div>
          <span className="progress-bar-label" aria-hidden="true">
            {data.progressCurrent} / {data.progressTotal} pages
          </span>
        </>
      )}
      <p className="muted small">Multi-page audits take 1–3 minutes depending on site size.</p>
      <p className="muted small">Safe to leave or refresh this page — the scan keeps running in the background.</p>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────────────── */

/**
 * The conversion engine. Shown to anonymous visitors after their scan completes but before
 * they submit an email. They see:
 *   - Compliance score + per-severity counts (real signal — proves the scan is real)
 *   - Top 3 violations with rule + WCAG ref (teaser — proves the report has substance)
 *   - Email form gating the full PDF + plain-English remediations
 *
 * What's deliberately NOT shown: plain-English explanations, code examples, EAA citations,
 * PDF download link. Those land in their inbox after they submit the form.
 */
function AnonymousScanSummaryView({ data, accessToken }: { data: ScanDetail; accessToken: string }) {
  const [email, setEmail] = useState('');
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const submit = useMutation({
    mutationFn: () => scansApi.submitEmailForReport(data.id, accessToken, email.trim(), marketingConsent),
    onSuccess: () => {
      // The endpoint queued the email send. Refetch the scan so the view flips from
      // anonymous_locked → anonymous_unlocked and the full violations + PDF link appear.
      queryClient.invalidateQueries({ queryKey: ['scan', data.id] });
    },
    onError: (err: unknown) => {
      if (err instanceof ApiError) {
        if (err.status === 409) {
          setErrorMessage(
            "An email has already been recorded for this scan. Check your inbox — the report link was sent to that address.",
          );
          return;
        }
        setErrorMessage(err.detail || err.title);
        return;
      }
      setErrorMessage('Something went wrong. Please try again.');
    },
  });

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    setErrorMessage(null);
    if (!email.trim()) return;
    submit.mutate();
  };

  return (
    <div className="page scan-summary">
      <header>
        <h1>Scan complete for {data.targetUrl}</h1>
        <p className="muted">{data.pageTitle}</p>
      </header>

      <div className="score-card">
        <div className="score-big">{data.complianceScore}<span>/ 100</span></div>
        <p>Compliance score (WCAG 2.1 AA)</p>
      </div>

      <div className="severity-row">
        <div className="tile critical"><strong>{data.criticalCount}</strong><span>Critical</span></div>
        <div className="tile serious"><strong>{data.seriousCount}</strong><span>Serious</span></div>
        <div className="tile moderate"><strong>{data.moderateCount}</strong><span>Moderate</span></div>
        <div className="tile minor"><strong>{data.minorCount}</strong><span>Minor</span></div>
      </div>

      {data.totalViolations > 0 && (
        <section className="violations">
          <h2>Top findings (showing {data.violations.length} of {data.totalViolations})</h2>
          <ul>
            {data.violations.map((v, i) => (
              <li key={`${v.ruleId}-${i}`} className={`v-${v.impact.toLowerCase()}`}>
                <strong>{v.shortDescription}</strong>
                <span className="muted small"> · {v.wcag} · {v.ruleId}</span>
              </li>
            ))}
          </ul>
          <p className="muted small">
            Full plain-English explanations, code examples, and EAA citations are in the PDF
            we'll email you.
          </p>
        </section>
      )}

      <section className="email-gate" aria-labelledby="email-gate-heading">
        <h2 id="email-gate-heading">Get the full PDF report</h2>
        <p>
          Detailed plain-English remediations, code-level fix examples, EAA / WCAG citations,
          and a ready-to-publish accessibility statement — all in one PDF, delivered in under
          60 seconds.
        </p>

        <form onSubmit={onSubmit} className="email-gate-form">
          <label htmlFor="report-email" className="sr-only">Email address</label>
          <input
            id="report-email"
            type="email"
            inputMode="email"
            autoComplete="email"
            placeholder="you@yourcompany.com"
            value={email}
            onChange={e => setEmail(e.target.value)}
            required
            disabled={submit.isPending}
          />
          <button type="submit" className="cta" disabled={submit.isPending || !email.trim()}>
            {submit.isPending ? 'Sending…' : 'Send my report'}
          </button>
        </form>

        <label className="email-gate-consent">
          <input
            type="checkbox"
            checked={marketingConsent}
            onChange={e => setMarketingConsent(e.target.checked)}
            disabled={submit.isPending}
          />
          <span>
            Email me one follow-up if EU Accessibility Act enforcement rules change for sites
            like this one. No newsletter, no marketing — only if something relevant happens.
          </span>
        </label>

        <p className="muted small">
          We'll send the report (lawful basis: contract performance, GDPR Art. 6(1)(b)). The
          optional checkbox above is separate consent. <Link to="/privacy">Privacy policy</Link>.
          We never sell or share emails.
        </p>

        {errorMessage && <p className="error" role="alert">{errorMessage}</p>}
      </section>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────────────── */

/**
 * Owned and anonymous-unlocked share the full report layout. Anonymous-unlocked skips the
 * certification / badge / domain-verification surfaces (those are owned-account features).
 */
function FullScanReportView({
  data,
  accessToken,
  queryClient,
}: {
  data: ScanDetail;
  accessToken: string | undefined;
  queryClient: ReturnType<typeof useQueryClient>;
}) {
  return (
    <div className="page scan-result">
      <header>
        <h1>{data.pageTitle ?? data.targetUrl}</h1>
        <p className="muted">{data.targetUrl}</p>
        {data.viewMode === 'anonymous_unlocked' && data.anonymousEmail && (
          <p className="muted small">
            Report emailed to <strong>{data.anonymousEmail}</strong>. You can also download
            it from this page anytime in the next 30 days.
          </p>
        )}
      </header>

      <div className="score-card">
        <div className="score-big">{data.complianceScore}<span>/ 100</span></div>
        <p>Compliance score (WCAG 2.1 AA)</p>
      </div>

      <div className="severity-row">
        <div className="tile critical"><strong>{data.criticalCount}</strong><span>Critical</span></div>
        <div className="tile serious"><strong>{data.seriousCount}</strong><span>Serious</span></div>
        <div className="tile moderate"><strong>{data.moderateCount}</strong><span>Moderate</span></div>
        <div className="tile minor"><strong>{data.minorCount}</strong><span>Minor</span></div>
      </div>

      <div className="downloads">
        <a className="cta" href={scansApi.reportPdfUrl(data.id, accessToken)} download>Download full report (PDF)</a>
        <a className="cta secondary" href={scansApi.statementHtmlUrl(data.id, accessToken)} download>Download accessibility statement (HTML)</a>
      </div>

      {data.viewMode === 'owned' && data.complianceScore >= 80 && data.tier && data.verificationToken && (
        <>
          <BadgeEmbedSection
            verificationToken={data.verificationToken}
            score={data.complianceScore}
            tier={data.tier}
            badgeIssued={data.badgeIssued}
          />
          <DomainVerificationPanel
            scanId={data.id}
            targetUrl={data.targetUrl}
            verificationToken={data.verificationToken}
            domainVerifiedAt={data.domainVerifiedAt}
            domainVerificationMethod={data.domainVerificationMethod}
            onVerified={() => queryClient.invalidateQueries({ queryKey: ['scan', data.id] })}
          />
        </>
      )}

      {data.viewMode === 'anonymous_unlocked' && (
        <section className="upgrade-prompt">
          <h2>Want to scan more URLs?</h2>
          <p>
            Sign up for a paid plan to remove the 3-scan-per-hour limit, save your scan
            history, and (for sites scoring ≥80) issue an embeddable compliance badge.
          </p>
          <Link to="/pricing" className="cta">See pricing</Link>
        </section>
      )}

      <section className="violations">
        <h2>Findings</h2>
        {data.violations.length === 0 ? (
          <p className="muted">No violations detected by automated scanning. Manual review is still recommended.</p>
        ) : (
          <ul>
            {data.violations.map((v, i) => (
              <li key={`${v.ruleId}-${i}`} className={`v-${v.impact.toLowerCase()}`}>
                <strong>{v.shortDescription}</strong>
                <span className="muted small"> &middot; {v.wcag} &middot; {v.ruleId}</span>
                {v.pageUrl && (
                  <p className="muted small">
                    First seen on{' '}
                    <a href={v.pageUrl} target="_blank" rel="noopener noreferrer">{v.pageUrl}</a>
                  </p>
                )}
                {v.plainEnglishExplanation && <p>{v.plainEnglishExplanation}</p>}
                {v.eaaQuote && (
                  <blockquote className="eaa-cite">
                    <span className="eaa-cite-label">{v.eaaSection ?? 'Directive 2019/882'}</span>
                    <q>{v.eaaQuote}</q>
                  </blockquote>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
